import React, { useCallback, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { Box, CircularProgress, Stack, styled } from "@mui/material";
import { RoleComponentGuard } from "components/guards/Role-component.guard";
import { useActionHook, useDidUpdate, useModals, useRoleGuardHook } from "hooks";
import { pickBy } from "lodash";
import {
  useDealActions,
  useDealQueriesSelect,
  usePickUserPermissions,
  useTaskManageActions,
  useUserIdSelector,
  useUserRoleSelector
} from "store";
import { IDealPreview, IGetDealsData, UserPermissionType } from "store/types";

import { RoleName } from "../../../../constants";
import { AddDealCard } from "./AddDealCard";
import { Card } from "./Card";

const StyledGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(min(280px, 100%), 1fr))",
  rowGap: "10px",
  columnGap: "10px",
  width: "100%",
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr"
  }
}));

export const CardView: React.FC = () => {
  const [data, setData] = useState<IDealPreview[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const { getDealsAction } = useTaskManageActions();
  const { getDealsCardsAction, deleteDealAction } = useDealActions();
  const { action: deleteDeal } = useActionHook(deleteDealAction, {
    onSuccess: getDealsAction
  });
  const dealQueries = useDealQueriesSelect();
  const { deal_title, order_by, filters } = dealQueries || {};
  const { deleteModal } = useModals();
  const currentUserId = useUserIdSelector();
  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;
  const role = useUserRoleSelector();

  const getDeal = useCallback(
    async (data: IGetDealsData & { turnOnLoading?: boolean }) => {
      try {
        if (data.turnOnLoading) {
          setLoading(true);
        }
        const { data: deals, last_page } = await getDealsCardsAction(data);
        if (data.page! >= last_page) {
          setHasMore(false);
        }

        setPage((prevState) => prevState + 1);
        setData((prevState) => [...prevState, ...deals]);
      } finally {
        if (data.turnOnLoading) {
          setLoading(false);
        }
      }
    },
    [setPage, hasMore]
  );

  const resetParams = useCallback(() => {
    setData([]);
    setPage(1);
    setLoading(false);
    setHasMore(true);
  }, []);

  // Initial API call
  useEffect(() => {
    const filtersPayload = pickBy(filters, (value) => value.length > 0);
    getDeal({
      filters: filtersPayload,
      deal_title,
      order_by,
      page: 1,
      turnOnLoading: true
    });
  }, []);

  // Listen queries, reset params and call api with default params
  useDidUpdate(() => {
    const filtersPayload = pickBy(filters, (value) => value.length > 0);
    resetParams();
    getDeal({
      filters: filtersPayload,
      deal_title,
      order_by,
      page: 1
    });
  }, [dealQueries]);

  const onClickDelete = useRoleGuardHook(
    (id: number, title: string) => {
      deleteModal({
        action: async () => {
          const dealId = await deleteDeal(id);
          setData(data.filter((deal: IDealPreview) => deal.id !== dealId));
        },
        element: "DELETE",
        message: `Are you sure you want to delete ${title}?`
      });
    },
    [RoleName.Admin, RoleName.Analyst]
  );

  return (
    <Box
      overflow="hidden"
      width="100%"
      sx={{ flexGrow: 1, borderRadius: "8px", border: "1px solid #dbe8f5", bgcolor: "#f6f8fc", p: "10px" }}
    >
      <InfiniteScroll
        loader={
          <Stack height="100%" justifyContent="center" alignItems="center">
            <CircularProgress size={150} />
          </Stack>
        }
        next={() =>
          getDeal({
            deal_title,
            order_by,
            page,
            filters: filters
          })
        }
        height="100%"
        hasMore={hasMore}
        dataLength={data.length}
      >
        {!loading && (
          <StyledGrid>
            <RoleComponentGuard
              availableRoles={[RoleName.Admin, RoleName.Analyst]}
              hideChild={isViewerPermission || noAccessPermission}
            >
              <Box sx={{ display: "flex", height: "100%", minWidth: 0, width: "100%" }}>
                <AddDealCard />
              </Box>
            </RoleComponentGuard>
            {data.map((dealPreview: IDealPreview, index) => (
              <Box key={dealPreview.id || index} sx={{ display: "flex", height: "100%", minWidth: 0, width: "100%" }}>
                <Card
                  {...(onClickDelete &&
                    (currentUserId === dealPreview.user_id || role?.name === RoleName.Admin) && { onClickDelete })}
                  {...dealPreview}
                />
              </Box>
            ))}
          </StyledGrid>
        )}
      </InfiniteScroll>
    </Box>
  );
};
